import React, {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';

import { ScreenReaderUpdateText, SaveEmailSubmitButtonGroup } from 'components';
import { SegmentedControlQuestion } from 'scenes/common/components/Questions';
import { ApiData, Excess, SingleFieldStepProps } from 'scenes/common/types';

import styled from 'styled-components';
import { filterOptionsToDisplay } from 'scenes/common/helpers/expandableOptionDisplay';
import ScreenReaderExcessUpdate from './ScreenReaderExcessUpdate';

type ReduceMarginProps = {
  hasDisclaimer?: boolean;
};

const ReduceMargin = styled.div`
  ${(props: ReduceMarginProps) =>
    props.hasDisclaimer ? '.p-form-group { margin: 0 0 0.75rem; }' : ``}
`;

const SHOW_MORE_TYPE = 'showMore';
const EXCESS = 'excess';
const SHOW_MORE_OPTION = {
  id: '',
  type: SHOW_MORE_TYPE,
  amount: 0,
};

const excessToDisplayOption = (excess: Excess) => ({
  label: excess.type !== SHOW_MORE_TYPE ? `$${excess.amount}` : 'Show more',
  value: excess,
});

const excessesToDisplayOptions = (excesses: Excess[]) =>
  excesses.map((excess) => excessToDisplayOption(excess));

const ExcessStep: FunctionComponent<
  Pick<
    SingleFieldStepProps<Excess>,
    'question' | 'helpText' | 'helpLink' | 'defaultStateValue' | 'onNext'
  > & {
    ariaLabel?: string;
    options: Excess[];
    quoteDetails: ApiData<unknown>;
    coverType: string;
    price: string;
    onUpdateExcess: (excess: Excess) => void;
    onClickSaveQuoteModal?: () => void;
    disclaimer?: JSX.Element;
  }
> = ({
  question,
  helpText,
  helpLink,
  defaultStateValue,
  options,
  coverType,
  price,
  onNext,
  onUpdateExcess,
  onClickSaveQuoteModal,
  disclaimer,
}) => {
  const {
    formState: hooksForm,
    handleSubmit,
    control,
    setValue,
    getValues,
    errors,
  } = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const NUM_OPTIONS_TO_DISPLAY = 5;

  const [showAll, setShowAll] = useState(
    options.length <= NUM_OPTIONS_TO_DISPLAY
  );

  useEffect(() => {
    setValue(EXCESS, defaultStateValue);
  }, [defaultStateValue, setValue]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function): void => {
      const optionValue = (event.target.value as unknown) as Excess;
      if (optionValue.type !== SHOW_MORE_OPTION.type) {
        // Ie if not 'show more'
        onChange(event);
        onUpdateExcess(getValues(EXCESS));
      } else {
        setShowAll(true);
      }
    },
    [getValues, onUpdateExcess]
  );

  const onSubmit = useCallback(() => {
    onNext({});
  }, [onNext]);

  return (
    <ReduceMargin hasDisclaimer={!!disclaimer}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <SegmentedControlQuestion
          errors={errors}
          control={control}
          stateKey={EXCESS}
          options={
            showAll
              ? excessesToDisplayOptions(options)
              : filterOptionsToDisplay(
                  excessesToDisplayOptions(options),
                  excessToDisplayOption(SHOW_MORE_OPTION),
                  NUM_OPTIONS_TO_DISPLAY,
                  excessToDisplayOption(defaultStateValue),
                  true
                )
          }
          defaultValue={defaultStateValue}
          changeHandler={handleChange}
          question={question}
          validationRules={{
            required: true,
            validate: {
              oneOf: (value: Excess) =>
                excessesToDisplayOptions(options).some(
                  (option) => option.value.id === value.id
                ),
            },
          }}
          helpText={helpText}
          helpLink={helpLink}
        />
        <ScreenReaderUpdateText />
        <ScreenReaderExcessUpdate
          coverType={coverType}
          price={price}
          excess={defaultStateValue}
        />
        {disclaimer}
        <SaveEmailSubmitButtonGroup
          onClickSaveQuoteModal={onClickSaveQuoteModal}
          submitEnabled={hooksForm.isValid}
        />
      </form>
    </ReduceMargin>
  );
};

export default ExcessStep;
